import globalAxios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import {
  AxiosCacheInstance,
  AxiosStorage,
  buildMemoryStorage,
  setupCache
} from 'axios-cache-interceptor'
import { setAxiosOnlineState } from '@/common/composables/useOffline'
import { ref } from 'vue'
import AiStreamingApi from '../../AiStreamingApi/aiStreamingApi'
import {
  AuthApi,
  BillingApi,
  AllowlistApi,
  BlocklistApi,
  CalendarGroupsApi,
  CalendarListApi,
  CalendarsApi,
  Configuration,
  EventsApi,
  FoldersApi,
  LabelsApi,
  MeApi,
  MoveApi,
  NotesApi,
  PromosApi,
  PushDevicesApi,
  TasksApi
} from '../../api'
import { Capacitor } from '@capacitor/core'
import { version } from '../../../../package.json'

function applyMobileUserAgent(axios: AxiosInstance) {
  const platform = Capacitor.getPlatform()

  // On native mobile, CapacitorHttp may use native networking where default UA
  // does not include browser tokens our backend expects. Set deterministic UA.
  if (platform === 'android') {
    axios.defaults.headers.common['User-Agent'] = `nocal-mobile-android/${version} Android`
    return
  }

  if (platform === 'ios') {
    axios.defaults.headers.common['User-Agent'] = `nocal-mobile-ios/${version} iPhone`
  }
}

export const ENDPOINTS = {
  production: {
    api: 'https://api.nocal.app',
    auth: 'https://auth.nocal.app',
    rt: 'wss://rt.nocal.app',
    // Marketing/Nuxt site (Calendar Hell lives here).
    web: 'https://nocal.app'
  },
  development: {
    // api: 'https://api.nocal.app',
    // auth: 'https://auth.nocal.app',
    // rt: 'wss://rt.nocal.app'
    api: 'http://127.0.0.1:8000', // localhost was having issues with the node side of electron.
    auth: 'http://127.0.0.1:8000',
    rt: 'ws://127.0.0.1:1234',
    // Local Nuxt dev server for the website (Calendar Hell endpoint). Brian may
    // change this port — update here if the Nuxt dev server runs elsewhere.
    web: 'http://localhost:3000'
  }
} as const

export interface WorkspaceStatePayload {
  revision: number
  changed_at: string
}

// Axios defaults to no timeout, which on Android means a request can hang
// forever: CapacitorHttp proxies GETs through WebViewLocalServer, which sets no
// connect or read timeout, so a half-open socket (doze, wifi/cellular handoff,
// VPN drop) yields a promise that never settles. Callers awaiting one are stuck
// with no way back — that is what pinned the mobile inbox on its skeleton.
// Streaming lives in AiStreamingApi over EventSource, so nothing long-lived
// rides this instance.
const REQUEST_TIMEOUT_MS = 30_000

function createAxiosInstance(cacheTtl?: number, axiosCache?: AxiosStorage) {
  let axios: AxiosInstance | AxiosCacheInstance = globalAxios.create({
    timeout: REQUEST_TIMEOUT_MS
  })
  let cacheStorage: AxiosStorage | null = null
  if (cacheTtl) {
    cacheStorage = axiosCache ?? buildMemoryStorage()

    axios = setupCache(axios, {
      ttl: cacheTtl,
      storage: cacheStorage
    }) as unknown as AxiosInstance
  }

  return {
    axios,
    axiosCache: cacheStorage
  }
}

export function useNocalApi(
  env: 'production' | 'development',
  cacheTtl?: number,
  axiosCache?: AxiosStorage
) {
  const isProduction = ref(env === 'production')
  const { axios, axiosCache: axiosCacheInstance } = createAxiosInstance(cacheTtl, axiosCache)
  applyMobileUserAgent(axios)

  /**
   * Clearing the cache storage alone strands every request that is currently
   * waiting on an identical in-flight one, and stranded means forever.
   *
   * axios-cache-interceptor deduplicates concurrent identical GETs: the first
   * marks the storage entry `loading` and registers a deferred in
   * `axios.waiting`; the rest park on that deferred. Wiping the storage
   * mid-flight puts the entry in a state the response interceptor doesn't
   * recognise, and its recovery is `waiting.delete(id); return response` — the
   * deferred is dropped without ever being resolved or rejected. The waiters
   * hold the object directly, so they are left awaiting a promise nothing can
   * settle. No error, no retry, no second request: from the outside it looks
   * exactly like a screen that loaded fine and then never finished, which is
   * how the mobile inbox came to sit on its skeleton with a clean network tab.
   *
   * Rejecting them first is the documented way back: a waiter whose deferred
   * rejects re-issues the request (`catch { return onFulfilled(config) }` in
   * the request interceptor), which is what we want anyway — the cache was just
   * cleared, so a fresh fetch is the correct outcome.
   *
   * This is the interceptor-level twin of the adapter-level hang the 30s
   * timeout above fixes. The timeout can't reach this one: the waiter never
   * gets as far as issuing a request.
   */
  const clearCacheAndReleaseWaiters = () => {
    const waiting = (axios as AxiosCacheInstance).waiting
    if (waiting) {
      for (const deferred of waiting.values()) {
        deferred.reject(new Error('Request cache cleared while waiting on an in-flight request'))
      }
      waiting.clear()
    }
    axiosCacheInstance?.clear?.()
  }

  // Axios interceptors for API-specific failure detection
  // This catches cases where network interface is up but API is unreachable
  // Updates the global offline state via setAxiosOnlineState
  // Each process (main and renderer) has its own axios instance and will detect offline independently
  axios.interceptors.response.use(
    (response) => {
      // If we get a successful response (not cached), we're definitely online
      if (!response.cached) {
        setAxiosOnlineState(false)
      }

      return response
    },
    (error) => {
      // A canceled request says nothing about connectivity — don't flip state.
      if (error.code !== 'ERR_CANCELED') {
        if (error.response === undefined) {
          // Network errors (no response) indicate API is unreachable
          // This is an additional check beyond navigator.onLine
          console.warn('[axios] Network error (offline):', error.message || error)
          setAxiosOnlineState(true)
        } else {
          // Any HTTP response — even an error status — proves the API is
          // reachable, which is exactly what this state tracks. Without this,
          // an erroring-but-reachable API (e.g. a backend 5xx incident) leaves
          // the offline banner stuck and the recovery probe looping forever,
          // since only a 2xx would otherwise clear the state.
          setAxiosOnlineState(false)
        }
      }
      return Promise.reject(error)
    }
  )

  const endpoints = ref(isProduction.value ? ENDPOINTS.production : ENDPOINTS.development)
  const config = new Configuration({
    basePath: endpoints.value.api
  })

  const aiStreaming = new AiStreamingApi(endpoints.value.api, null)

  const accessTokenSet = ref(false)

  function setAccessToken(token: string | null) {
    if (token === null) {
      delete config.accessToken
      accessTokenSet.value = false
    } else {
      config.accessToken = token
      aiStreaming.setAccessToken(token)
      accessTokenSet.value = true
    }
  }

  function requireAccessToken() {
    if (!accessTokenSet.value) {
      throw new Error('Access token not set. Call setAccessToken() before using API services.')
    }
  }

  function setEnvironment(env: 'production' | 'development') {
    config.basePath = env === 'production' ? ENDPOINTS.production.api : ENDPOINTS.development.api
    axios.defaults.baseURL =
      env === 'production' ? ENDPOINTS.production.api : ENDPOINTS.development.api
    endpoints.value = env === 'production' ? ENDPOINTS.production : ENDPOINTS.development
    aiStreaming.setBaseUrl(endpoints.value.api)
    isProduction.value = env === 'production'
  }

  return {
    config,
    axios,
    axiosCache: axiosCacheInstance,
    isProduction,
    endpoints,
    isAuthenticated: accessTokenSet,
    setAccessToken,
    setEnvironment,
    clearCache: clearCacheAndReleaseWaiters,
    calendars: () => {
      requireAccessToken()
      return new CalendarsApi(config, undefined, axios)
    },
    calendarList: () => {
      requireAccessToken()
      return new CalendarListApi(config, undefined, axios)
    },
    calendarGroups: () => {
      requireAccessToken()
      return new CalendarGroupsApi(config, undefined, axios)
    },
    labels: () => {
      requireAccessToken()
      return new LabelsApi(config, undefined, axios)
    },
    events: () => {
      requireAccessToken()
      return new EventsApi(config, undefined, axios)
    },
    notes: () => {
      requireAccessToken()
      return new NotesApi(config, undefined, axios)
    },
    folders: () => {
      requireAccessToken()
      return new FoldersApi(config, undefined, axios)
    },
    tasks: () => {
      requireAccessToken()
      return new TasksApi(config, undefined, axios)
    },
    blocklist: () => {
      requireAccessToken()
      return new BlocklistApi(config, undefined, axios)
    },
    allowlist: () => {
      requireAccessToken()
      return new AllowlistApi(config, undefined, axios)
    },
    workspaceState: () => {
      requireAccessToken()
      return {
        check: (
          ifNoneMatch?: string | null,
          options?: AxiosRequestConfig
        ): Promise<AxiosResponse<WorkspaceStatePayload>> => {
          const token = config.accessToken
          if (typeof token !== 'string' || !token) {
            throw new Error(
              'Access token not set. Call setAccessToken() before using API services.'
            )
          }
          const existingHeaders = (options?.headers as Record<string, string> | undefined) ?? {}
          return axios.get<WorkspaceStatePayload>(`${config.basePath}/folders/workspace-state/`, {
            ...options,
            headers: {
              ...existingHeaders,
              ...(ifNoneMatch ? { 'If-None-Match': ifNoneMatch } : {}),
              Authorization: `Bearer ${token}`
            }
          })
        }
      }
    },
    move: () => {
      requireAccessToken()
      return new MoveApi(config, undefined, axios)
    },
    auth: () => new AuthApi(config, undefined, axios), // Auth API doesn't require token
    pushDevices: () => {
      requireAccessToken()
      return new PushDevicesApi(config, undefined, axios)
    },
    me: () => {
      requireAccessToken()
      return new MeApi(config, undefined, axios)
    },
    aiStreaming: () => {
      requireAccessToken()
      return aiStreaming
    },
    billing: () => {
      requireAccessToken()
      return new BillingApi(config, undefined, axios)
    },
    promos: () => {
      requireAccessToken()
      return new PromosApi(config, undefined, axios)
    },
    // Note import/export live here (not the generated client): regenerating to
    // pick them up pulls unrelated schema drift. Same shape as workspaceState —
    // a raw call on the shared axios with a manual bearer token. timeout:0 so a
    // large bulk archive isn't cut off by the shared default.
    notesExport: (): Promise<AxiosResponse<Blob>> => {
      requireAccessToken()
      const token = config.accessToken
      if (typeof token !== 'string' || !token) {
        throw new Error('Access token not set. Call setAccessToken() before using API services.')
      }
      return axios.get<Blob>(`${config.basePath}/notes/export/`, {
        params: { format: 'markdown' },
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
        timeout: 0,
        // Never serve the export from the GET cache; each export must be fresh.
        cache: false
      } as AxiosRequestConfig)
    },
    notesImport: (
      file: File,
      format: 'markdown' | 'enex',
      folderId?: string | null,
      duplicateStrategy?: 'new' | 'update' | null
    ): Promise<AxiosResponse<unknown>> => {
      requireAccessToken()
      const token = config.accessToken
      if (typeof token !== 'string' || !token) {
        throw new Error('Access token not set. Call setAccessToken() before using API services.')
      }
      const form = new FormData()
      form.append('file', file)
      form.append('format', format)
      // The destination folder the whole import nests under, for every format
      // (loose Markdown, ready-made .zip, and .enex). Absent means the account root.
      if (folderId) form.append('folder_id', folderId)
      // Markdown zip only: how to treat manifest notes that already exist —
      // 'new' copies, 'update' updates in place. Absent keeps legacy behavior.
      if (duplicateStrategy) form.append('duplicate_strategy', duplicateStrategy)
      // Let the browser set the multipart Content-Type (with boundary) itself.
      return axios.post(`${config.basePath}/notes/import/`, form, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 0
      } as AxiosRequestConfig)
    }
  }
}
